import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { PageLoading } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { LeadDashboard } from '../../types';

export default function LeadDashboardPage() {
  const toast = useToast();
  const [data, setData] = useState<LeadDashboard | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await api.get<LeadDashboard>('/leads/dashboard'));
      } catch (err) {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not load the dashboard.');
      }
    })();
  }, [toast]);

  const maxStage = data ? Math.max(1, ...data.byStage.map((s) => s.count)) : 1;

  return (
    <>
      <PageHeader title="Leads dashboard" subtitle="Where your pipeline stands." />
      <div className="page col gap-16">
        {data === null ? <PageLoading /> : (
          <>
            <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
              <StatCard label="Total leads" value={data.total} />
              <StatCard label="Open" value={data.outcome.open} tone="var(--warning)" />
              <StatCard label="Won" value={data.outcome.won} tone="var(--success)" />
              <StatCard label="Lost" value={data.outcome.lost} tone="var(--danger)" />
              <StatCard label="Win rate" value={data.winRate == null ? '—' : `${data.winRate}%`} tone="var(--brand)" />
            </div>

            <div className="card">
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                <strong>By stage</strong>
              </div>
              <div className="col gap-8" style={{ padding: 18 }}>
                {data.byStage.length === 0 ? (
                  <span className="small faint">No stages configured.</span>
                ) : data.byStage.map((s) => (
                  <div key={s.id} className="row gap-12" style={{ alignItems: 'center' }}>
                    <span className="small" style={{ width: 130, textAlign: 'right' }}>{s.label}</span>
                    <div style={{ flex: 1, background: 'var(--border)', borderRadius: 6, height: 20 }}>
                      <div style={{
                        width: `${(s.count / maxStage) * 100}%`, minWidth: s.count ? 20 : 0,
                        background: 'var(--brand)', height: '100%', borderRadius: 6,
                      }} />
                    </div>
                    <span className="small faint" style={{ width: 40 }}>{s.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                <strong>By owner</strong>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr><th>Owner</th><th className="num">Leads</th><th className="num">Won</th><th className="num">Lost</th></tr>
                  </thead>
                  <tbody>
                    {data.byOwner.length === 0 ? (
                      <tr><td colSpan={4} className="faint" style={{ padding: 18 }}>No leads yet.</td></tr>
                    ) : data.byOwner.map((o) => (
                      <tr key={o.id ?? 'unassigned'}>
                        <td className="primary">{o.name}</td>
                        <td className="num">{o.total}</td>
                        <td className="num">{o.won}</td>
                        <td className="num">{o.lost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div className="card card-pad" style={{ minWidth: 130, flex: 1 }}>
      <div className="small faint">{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: tone ?? 'var(--ink)' }}>{value}</div>
    </div>
  );
}
